import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ToastrService } from 'ngx-toastr';
import { InternalUserService } from '../services/internal-user.service';
import { ProfileService } from '../services/profile.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { Router, ActivatedRoute } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from '../components/confirm-dialog/confirm-dialog.component';
import { InternalUserV2Output } from '../services/models/internal-user.model';
import { ProfileOutput } from '../services/models/profile.model';
import { UserPermissionsService } from '../services/user-permissions.service';
import { HasPermissionDirective } from '../directives/has-permission.directive';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';

enum Tabs {
  Usuarios = 'usuarios',
  Perfis = 'perfis'
}

@Component({
  selector: 'app-internal-users',
  standalone: true,
  imports: [FormsModule, CommonModule, HasPermissionDirective, TranslocoModule],
  templateUrl: './internal-users.component.html',
  styleUrl: './internal-users.component.css'
})
export class InternalUsersComponent implements OnInit {
  Tabs = Tabs;
  activeTab: string = Tabs.Usuarios;
  users: InternalUserV2Output[] = [];
  profiles: ProfileOutput[] = [];
  profileMap: Map<string, string> = new Map(); // Mapa de ID -> Nome para exibição rápida
  dropdownOpen: { [key: string]: boolean } = {};
  profileDropdownOpen: { [key: string]: boolean } = {};

  constructor(
    private internalUserService: InternalUserService,
    private profileService: ProfileService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService,
    private router: Router,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private permissionsService: UserPermissionsService,
    private transloco: TranslocoService
  ) { }

  ngOnInit(): void {
    // Carregar perfis primeiro para criar o mapa, depois carregar usuários
    this.loadProfiles().then(() => {
      this.search();
    });
    
    // Verificar se há query param para definir aba ativa
    this.route.queryParams.subscribe(params => {
      const tab = params['tab'];
      if (tab === 'perfis') {
        this.activeTab = Tabs.Perfis;
      } else {
        this.activeTab = Tabs.Usuarios;
      }
    });
  }

  setActiveTab(tab: Tabs): void {
    this.activeTab = tab;
  }

  search(): void {
    // Garantir que os perfis estejam carregados antes de buscar usuários
    // Se o mapa estiver vazio, recarregar perfis primeiro
    if (this.profileMap.size === 0) {
      this.loadProfiles().then(() => {
        this.loadUsers();
      });
    } else {
      this.loadUsers();
    }
  }

  private loadUsers(): void {
    this.spinner.show();
    this.internalUserService.getAll()
      .subscribe({
        next: response => {
          this.users = response;
          this.spinner.hide();
        },
        error: error => {
          console.error('Erro ao carregar usuários:', error);
          this.toastr.error(this.transloco.translate('users.toast.loadUsersError'));
          this.spinner.hide();
        }
      });
  }

  createUser(): void {
    if (!this.permissionsService.hasPermission('users.create')) {
      this.toastr.error(this.transloco.translate('users.toast.noCreatePermission'), this.transloco.translate('toasts.accessDenied'));
      return;
    }
    this.router.navigate(['/users/create']);
  }

  editUser(userId: string): void {
    if (!this.permissionsService.hasPermission('users.edit')) {
      this.toastr.error(this.transloco.translate('users.toast.noEditPermission'), this.transloco.translate('toasts.accessDenied'));
      return;
    }
    this.router.navigate(['/users/edit', userId]);
  }

  toggleDropdown(user: InternalUserV2Output): void {
    this.dropdownOpen[user.id] = !this.dropdownOpen[user.id];
  }

  deleteUser(user: InternalUserV2Output): void {
    if (!this.permissionsService.hasPermission('users.delete')) {
      this.toastr.error(this.transloco.translate('users.toast.noDeletePermission'), this.transloco.translate('toasts.accessDenied'));
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      maxWidth: '90vw',
      data: {
        title: this.transloco.translate('users.confirmDeleteTitle'),
        message: this.transloco.translate('users.confirmDeleteUser', { email: user.email })
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.spinner.show();
        this.internalUserService.delete(user.id)
          .subscribe({
            next: () => {
              this.toastr.success(this.transloco.translate('users.toast.userDeleted'));
              this.search();
              this.toggleDropdown(user);
              this.spinner.hide();
            },
            error: error => {
              console.error('Erro ao excluir usuário:', error);
              this.toastr.error(this.transloco.translate('users.toast.userDeleteError'));
              this.spinner.hide();
            }
          });
      }
    });
  }

  getEmailValidatedBadgeClass(emailValidated: boolean): string {
    return emailValidated
      ? 'bg-green-200 text-green-800'
      : 'bg-red-200 text-red-800';
  }

  getEmailValidatedText(emailValidated: boolean): string {
    return this.transloco.translate(emailValidated ? 'users.validated' : 'users.notValidated');
  }

  getTwoFactorBadgeClass(twoFactorEnabled: boolean): string {
    return twoFactorEnabled
      ? 'bg-green-200 text-green-800'
      : 'bg-red-200 text-red-800';
  }

  getTwoFactorText(twoFactorEnabled: boolean): string {
    return this.transloco.translate(twoFactorEnabled ? 'users.active' : 'users.inactive');
  }

  getProfileIdsText(profileIds: string[]): string {
    if (!profileIds || profileIds.length === 0) {
      return '-';
    }
    // Mapear IDs para nomes usando o profileMap
    const profileNames = profileIds
      .map(id => this.profileMap.get(id) || id) // Se não encontrar o nome, usa o ID como fallback
      .filter(name => name); // Remove valores vazios
    
    return profileNames.length > 0 ? profileNames.join(', ') : '-';
  }

  // Métodos para Perfis
  loadProfiles(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.spinner.show();
      this.profileService.getAll()
        .subscribe({
          next: response => {
            this.profiles = response;
            // Criar mapa de ID -> Nome para busca rápida
            this.profileMap.clear();
            response.forEach(profile => {
              this.profileMap.set(profile.id, profile.name);
            });
            this.spinner.hide();
            resolve();
          },
          error: error => {
            console.error('Erro ao carregar perfis:', error);
            this.toastr.error(this.transloco.translate('users.toast.loadProfilesError'));
            this.spinner.hide();
            reject(error);
          }
        });
    });
  }

  createProfile(): void {
    this.router.navigate(['/users/profiles/create']);
  }

  editProfile(profileId: string): void {
    this.router.navigate(['/users/profiles/edit', profileId]);
  }

  toggleProfileDropdown(profile: ProfileOutput): void {
    this.profileDropdownOpen[profile.id] = !this.profileDropdownOpen[profile.id];
  }

  deleteProfile(profile: ProfileOutput): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      maxWidth: '90vw',
      data: {
        title: this.transloco.translate('users.confirmDeleteTitle'),
        message: this.transloco.translate('users.confirmDeleteProfile', { name: profile.name })
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.spinner.show();
        this.profileService.delete(profile.id)
          .subscribe({
            next: () => {
              this.toastr.success(this.transloco.translate('users.toast.profileDeleted'));
              this.loadProfiles();
              this.toggleProfileDropdown(profile);
              this.spinner.hide();
            },
            error: error => {
              console.error('Erro ao excluir perfil:', error);
              this.handleProfileError(error, 'excluir');
              this.spinner.hide();
            }
          });
      }
    });
  }

  getStatusBadgeClass(isActive: boolean): string {
    return isActive
      ? 'bg-green-200 text-green-800'
      : 'bg-red-200 text-red-800';
  }

  getStatusText(isActive: boolean): string {
    return this.transloco.translate(isActive ? 'users.active' : 'users.inactive');
  }

  getPermissionKeysText(permissionKeys: string[]): string {
    if (!permissionKeys || permissionKeys.length === 0) {
      return '-';
    }
    return permissionKeys.join(', ');
  }

  handleProfileError(error: any, action: string): void {
    if (error.status === 400) {
      const errorMessage = error.error?.message || this.transloco.translate('users.toast.profileDataError');
      this.toastr.error(errorMessage);
    } else if (error.status === 404) {
      this.toastr.error(this.transloco.translate('users.toast.profileNotFound'));
    } else {
      this.toastr.error(this.transloco.translate('users.toast.profileGenericError'));
    }
  }
}

